import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Close, Download, Refresh } from "@mui/icons-material";

interface DocumentsModalProps {
  open: boolean;
  onClose: () => void;
  onDownloadInvoice: () => void;
  onSendInvoice: () => void;
  onRegenerateInvoice: () => Promise<{ success: boolean; message: string }>;
  order: any;
}

const DocumentsModal: React.FC<DocumentsModalProps> = ({
  open,
  onClose,
  onDownloadInvoice,
  onRegenerateInvoice,
  order,
}) => {
  const hasInvoiceUrl = Boolean(order?.invoicePath);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultSuccess, setResultSuccess] = useState(false);
  const [resultMessage, setResultMessage] = useState("");

  useEffect(() => {
    if (!open) {
      setIsRegenerating(false);
      setResultOpen(false);
      setResultMessage("");
    }
  }, [open]);

  const handleRegenerate = async () => {
    if (isRegenerating) return;
    setIsRegenerating(true);
    try {
      const result = await onRegenerateInvoice();
      setResultSuccess(Boolean(result?.success));
      setResultMessage(
        result?.message ||
          (result?.success
            ? "Invoice generated successfully."
            : "Failed to generate invoice.")
      );
      setResultOpen(true);
    } catch (error: any) {
      setResultSuccess(false);
      setResultMessage(
        error?.message || "Failed to generate invoice. Please try again."
      );
      setResultOpen(true);
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h6">Documents</Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Order Details
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Stock: {order?.stockName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Investor: {order?.investorName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Type: {order?.type}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Quantity: {order?.quantity}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <List>
            <ListItem
              disabled={!hasInvoiceUrl}
              onClick={hasInvoiceUrl ? onDownloadInvoice : undefined}
              sx={{
                borderRadius: 1,
                mb: 1,
                opacity: hasInvoiceUrl ? 1 : 0.5,
                cursor: hasInvoiceUrl ? "pointer" : "not-allowed",
                "&:hover": hasInvoiceUrl
                  ? {
                      backgroundColor: "rgba(25, 118, 210, 0.08)",
                    }
                  : {},
              }}
            >
              <ListItemIcon>
                <Download color={hasInvoiceUrl ? "primary" : "disabled"} />
              </ListItemIcon>
              <ListItemText
                primary="Download Invoice"
                secondary={
                  hasInvoiceUrl
                    ? "Download the invoice as PDF"
                    : "Invoice file is not available"
                }
              />
            </ListItem>
          </List>

          {!hasInvoiceUrl && (
            <Box
              sx={{
                mt: 1,
                mb: 2,
                p: 2,
                borderRadius: 1,
                backgroundColor: "#fafafa",
                border: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                Invoice is not present currently. You can regenerate it using
                the button below.
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={
                  isRegenerating ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Refresh />
                  )
                }
                onClick={handleRegenerate}
                disabled={isRegenerating}
                sx={{ textTransform: "none" }}
              >
                {isRegenerating ? "Regenerating..." : "Regenerate Invoice"}
              </Button>
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button onClick={onClose} variant="outlined">
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={resultOpen}
        onClose={() => setResultOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {resultSuccess ? "Invoice Generated" : "Invoice Generation Failed"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
            {resultMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setResultOpen(false)}
            variant="contained"
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentsModal;
